import React, { useState, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { FiArrowUp, FiSquare } from 'react-icons/fi';

interface MessageInputProps {
  onSendMessage: (message: string) => Promise<void>;
  onStopMessage: () => void;
  isLoading: boolean;
  pendingClarification?: {
    originalQuery: string;
    questions: string[];
  } | null;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, onStopMessage, isLoading, pendingClarification }) => {
  const [message, setMessage] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Handle input change and auto-resize textarea
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Auto-resize up to 5 lines maximum
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const lineHeight = 24; // approximate line height in pixels
      const maxHeight = lineHeight * 5; // 5 lines maximum
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`;
    }
  };

  // Handle message submission
  const handleSubmit = async () => {
    if (message.trim() && !isLoading) {
      const messageToSend = message.trim();
      
      // Clear message immediately when send is clicked
      setMessage('');
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      
      // Send the message
      await onSendMessage(messageToSend);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Handle focus events
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="space-y-3">
      {/* Clarification Notice */}
      {pendingClarification && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <div className="w-5 h-5 bg-yellow-400 dark:bg-yellow-500 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-white text-xs font-bold">?</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                Please provide clarification for: "{pendingClarification.originalQuery}"
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-300">
                Your next message will be used as clarification and appended to the original query.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Input Container */}
      <div className={`relative bg-white dark:bg-dark-secondary rounded-2xl shadow-sm pt-1 transition-colors duration-200 ${
        isFocused 
          ? 'border border-primary' 
          : 'border border-neutral-dark dark:border-dark-border'
      } ${pendingClarification ? 'border-yellow-300 dark:border-yellow-600' : ''}`}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={pendingClarification 
            ? "Please provide clarification..." 
            : "Ask about your shipment data..."
          }
          className="w-full resize-none outline-none py-3 px-4 pr-12 min-h-[44px] rounded-xl bg-transparent text-text-primary dark:text-dark-text placeholder-text-tertiary dark:placeholder-dark-textSecondary overflow-y-auto"
          rows={1}
          disabled={isLoading}
        />
        {/* Send/Stop Button */}
        {isLoading ? (
          <button
            onClick={onStopMessage}
            className="absolute right-3 bottom-3 p-2.5 rounded-full text-white bg-red-500 hover:bg-red-600 transition-colors"
            aria-label="Stop processing"
          >
            <FiSquare size={18} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!message.trim()}
            className={`absolute right-3 bottom-3 p-2.5 rounded-full text-white ${
              message.trim() 
                ? (pendingClarification ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-primary hover:bg-primary-dark')
                : 'bg-neutral-dark dark:bg-dark-accent cursor-not-allowed'
            } transition-colors`}
            aria-label={pendingClarification ? "Send clarification" : "Send message"}
          >
            <FiArrowUp size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
